@let entityId = entityId$ | async;
@let hideControls = hideControls$ | async;

@let isActive = entityId === entity().id;
@let isFocus = hoverState()?.hover || isActive || isSettingsMenuOpened();

<div
    #blockRoot
    class="block"
    [id]="displayId()"
    [attr.data-raw-id]="displayId()"
    [class.active]="isActive"
    [class.active-more-action]="isSettingsMenuOpened()"
    [webSingleHover]="{ threshold: 100 }"
    (hovered)="hovered($event)"
>
    @if (isFocus) {
        <div
            class="info-label-container"
            [class.hidden-shared-block-info-label]="isBlockHidden()"
            [class.row-controls]="itemControls().rowControls"
            [class.basic-controls-position]="itemControls().basicControlsPosition"
            [class.small-top-indent]="itemControls().smallTopIndent"
            [class.small-bottom-indent]="itemControls().smallBottomIndent"
            [class.bottom-inner-controls-position]="itemControls().bottomInnerControlsPosition"
            [class.bottom-outer-controls-position]="itemControls().bottomOuterControlsPosition"
        >
            @if (entity().meta.isShared) {
                <div
                    class="info-label shared-label"
                    [tooltip]="tooltipShared"
                    [placement]="'bottom'"
                    [tooltipAppendToBody]="true"
                >
                    <img src="./assets/img/websites/lpc-shared-block-icon-w.svg" alt="shared blocks" />
                    <ng-template #tooltipShared>
                        {{ hintText() | translate }}
                    </ng-template>
                </div>
            }
            @if (isPinnedTop() || isPinnedBottom()) {
                <div [tooltip]="tooltipPinned" [tooltipAppendToBody]="true" class="info-label">
                    <img
                        src="./assets/img/websites/lpc-pinned-block-label-icon.svg"
                        [alt]="'control_block_pinned' | translate"
                    />
                    <ng-template #tooltipPinned>
                        {{ "control_block_pinned" | translate }}
                    </ng-template>
                </div>
            }
            @if (hidden().desktop || hidden().tablet || hidden().mobile) {
                <div class="info-label" [tooltip]="tooltipHidden" [tooltipAppendToBody]="true">
                    <img [src]="hiddenIconPath()" [alt]="hiddenIconTooltip() | translate" />
                    <ng-template #tooltipHidden>
                        {{ hiddenIconTooltip() | translate }}
                    </ng-template>
                </div>
            }
        </div>
    }

    @if (isBlockHidden()) {
        <div class="hidden-block">
            {{ hiddenBlockText() | translate }}
        </div>
    } @else {
        <!--
            One binding per custom property, not one bag: each reads a single node
            of the signal form, so a padding edit rewrites four declarations rather
            than twenty.
        -->
        <div
            class="block-wrap specific-block-wrap"
            [class.collapses-on-mobile]="collapsesOnMobile()"
            [class.has-icon-right]="hasIconRight()"
            [style.--block-bg]="bg()"
            [style.--block-bg-position]="bgPosition()"
            [style.--block-bg-repeat]="bgRepeat()"
            [style.--block-bg-size]="bgSize()"
            [style.--block-bar-bg]="barBg()"
            [style.--block-padding-top]="paddingTop()"
            [style.--block-padding-bottom]="paddingBottom()"
            [style.--block-padding-top-mobile]="paddingTopMobile()"
            [style.--block-padding-right-mobile]="paddingRightMobile()"
            [style.--block-padding-bottom-mobile]="paddingBottomMobile()"
            [style.--block-padding-left-mobile]="paddingLeftMobile()"
            [style.--block-icon-color]="iconColor()"
            [style.--block-icon-size]="iconGlyphSize()"
            [style.--block-icon-box-size]="iconBoxSize()"
            [style.--block-icon-gap-right]="iconGapRight()"
            [style.--block-icon-gap-left]="iconGapLeft()"
            [style.--block-content-align]="contentAlign()"
            [style.--block-content-text-align]="contentTextAlign()"
            [style.--block-text-color]="textColor()"
        >
            @if (!(hideContentOnMobile() && isDeviceMobile())) {
                <div class="block-inner desktop-content">
                    <div>
                        @for (
                            section of entity().children;
                            track section.id;
                            let i = $index;
                            let first = $first;
                            let last = $last
                        ) {
                            <web-specific-section
                                [index]="i"
                                [parentEntity]="entity()"
                                [entity]="section"
                                [isFirst]="first"
                                [isLast]="last"
                                [blockHasLogo]="!!logoWidgetForSpecificBlock()"
                            />
                        }
                    </div>
                </div>
            }

            @if (hideContentOnMobile()) {
                <div class="mobile-content">
                    <div class="specific-top-nav">
                        <div
                            class="mobile-icon"
                            [class.sm-icon]="iconSize() === ESize.S"
                            [class.md-icon]="iconSize() === ESize.M"
                            [class.lg-icon]="iconSize() === ESize.L"
                        >
                            <div class="mobile-icon-lines"></div>
                        </div>

                        @if (logoWidgetForSpecificBlock()) {
                            <web-specific-widget [widget]="logoWidgetForSpecificBlock()" />
                        }

                        @let iconPositionType = iconPositionType$ | async;
                        @if (iconPositionType === "header") {
                            <web-shop-cart-icon-view />
                        }
                    </div>
                </div>
            }
        </div>
    }

    @if (hideControls) {
        @if (canAddTop() && hoverState()?.topThreshold) {
            <web-add-buttons
                type="top"
                [show]="true"
                [tooltipText]="'control_add_block' | translate"
                (addEvent)="addBlockTop()"
            />
        }
        @if (canAddBottom() && hoverState()?.bottomThreshold) {
            <web-add-buttons
                type="bottom"
                [show]="true"
                [tooltipText]="'control_add_block' | translate"
                (addEvent)="addBlockBottom()"
            />
        }

        @if (isFocus) {
            @let isAdmin = isAdmin$ | async;
            <web-specific-block-controls
                class="block-controls"
                [class.row-controls]="itemControls().rowControls"
                [class.basic-controls-position]="itemControls().basicControlsPosition"
                [class.small-top-indent]="itemControls().smallTopIndent"
                [class.small-bottom-indent]="itemControls().smallBottomIndent"
                [class.bottom-inner-controls-position]="itemControls().bottomInnerControlsPosition"
                [class.bottom-outer-controls-position]="itemControls().bottomOuterControlsPosition"
                [selectActionTooltip]="'control_block_settings' | translate"
                [isBlockHidden]="isBlockHidden()"
                [saveBlockToGallery]="!isAdmin"
                (copyEvent)="copyBlock()"
                (deleteEvent)="removeBlock()"
                (selectEvent)="selectBlock()"
                (toggleMoreActionState)="setSettingsMenuOpened($event)"
                (hideBlockEvent)="hideBlock()"
                (showBlockEvent)="showBlock()"
                (saveBlockToGalleryEvent)="saveBlockToGallery()"
            />
        }
    }
</div>
